import * as React from 'react';
import { Navigate, useLocation, Location } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactElement; // More specific than ReactNode for a single child component
}

// ProtectedRoute component that checks for authentication based on BFF context
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  // useAuth now provides isAuthenticated and loading based on backend session checks
  const { isAuthenticated, loading } = useAuth(); 
  const location: Location = useLocation(); // Get current location and type it

  // Show loading spinner while AuthContext is verifying auth status with the backend
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If not loading and not authenticated, redirect to login
  if (!isAuthenticated) {
    // Store the intended path to redirect after successful login
    const fullPath = location.pathname + location.search;
    sessionStorage.setItem('redirectAfterLogin', fullPath);
    
    // For shared data URLs, store the data in localStorage for persistence through SSO
    if (location.pathname === '/share' && location.search.includes('data=')) {
      const urlParams = new URLSearchParams(location.search);
      const dataParam = urlParams.get('data');
      if (dataParam) {
        localStorage.setItem('pendingSharedData', dataParam);
        localStorage.setItem('pendingSharedDataTimestamp', Date.now().toString());
      }
    }
    
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render the children if user is authenticated
  return children;
};

export default ProtectedRoute;
